// Module-local adapters for the legacy pages. Source data stays unescaped until rendering.
export function parseCalendarDate(value) {
  if (!value) return null;
  const s = String(value).trim();
  const iso = s.match(/^(\d{4})-(\d{2})-(\d{2})(?:$|[T ])/);
  const gviz = s.match(/^Date\((\d+),(\d+),(\d+)/);
  const parts = iso || gviz;
  if (!parts) return null;
  const year = +parts[1], month = +parts[2] - (iso ? 1 : 0), day = +parts[3];
  const date = new Date(year, month, day);
  return date.getFullYear() === year && date.getMonth() === month && date.getDate() === day ? date : null;
}

export function dateKey(date) {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
}

export function escapeHtml(value) {
  return String(value ?? '').replace(/[&<>"']/g, c => ({'&':'&amp;', '<':'&lt;', '>':'&gt;', '"':'&quot;', "'":'&#39;'}[c]));
}

export function safeUrl(value) {
  try {
    const url = new URL(String(value));
    return ['https:', 'http:'].includes(url.protocol) ? url.href : '';
  } catch { return ''; }
}

// Each synthetic profile is a platform/country account. Use its latest daily snapshot,
// then sum accounts. A falling follower count must remain a fall, not a historical max.
export function followerSnapshot(rows) {
  const accounts = new Map();
  for (const row of rows) {
    if (!row.date) continue;
    const key = JSON.stringify([row.platform.trim().toLowerCase(), (row.country || '').trim().toLowerCase()]);
    const previous = accounts.get(key);
    if (!previous || row.date > previous.date || (+row.date === +previous.date && row.followers > previous.followers)) accounts.set(key, row);
  }
  return [...accounts.values()].reduce((sum, row) => sum + (+row.followers || 0), 0);
}

export function csvText(rows) {
  const columns = [...new Set(rows.flatMap(row => Object.keys(row)))];
  const cell = value => {
    let text = value instanceof Date ? dateKey(value) : String(value ?? '');
    // Spreadsheet formula injection is possible even inside quoted CSV cells.
    if (/^[\s]*[=+@-]/.test(text) && typeof value !== 'number') text = "'" + text;
    return '"' + text.replaceAll('"', '""') + '"';
  };
  return [columns.map(cell).join(','), ...rows.map(row => columns.map(key => cell(row[key])).join(','))].join('\r\n');
}

export function downloadCsv(rows, name) {
  const url = URL.createObjectURL(new Blob(['\uFEFF', csvText(rows)], {type: 'text/csv;charset=utf-8'}));
  const link = document.createElement('a');
  link.href = url;
  link.download = name + '.csv';
  link.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

export function createPageRuntime(root) {
  const controller = new AbortController();
  let disposed = false;
  const resizeHandlers = new Map();
  const resize = () => { if (!disposed) resizeHandlers.forEach(callback => callback()); };
  function onResize(key, callback) {
    if (!resizeHandlers.size) window.addEventListener('resize', resize);
    resizeHandlers.set(key, callback);
  }
  function showError(message) {
    if (disposed) return;
    let panel = root.querySelector('[data-demo-error]');
    if (!panel) {
      panel = document.createElement('section');
      panel.className = 'o-state o-error';
      panel.dataset.demoError = '';
      panel.setAttribute('role', 'alert');
      const text = document.createElement('p');
      const retry = document.createElement('button');
      retry.className = 'o-btn';
      retry.textContent = 'Retry';
      retry.addEventListener('click', () => window.location.reload());
      panel.append(text, retry);
      root.prepend(panel);
    }
    panel.querySelector('p').textContent = message;
    root.querySelectorAll('#loading, #sl-loading').forEach(el => { el.style.display = 'none'; });
  }
  async function request(url, options = {}) {
    const headers = new Headers(options.headers);
    headers.set('Authorization', `Bearer ${localStorage.getItem('mp_token') || ''}`);
    const abort = () => controller.abort();
    options.signal?.addEventListener('abort', abort, {once: true});
    try {
      const response = await fetch(url, {...options, headers, signal: controller.signal});
      if (!response.ok) throw new Error(response.status === 401 ? 'Your session has expired. Sign in again to load the dashboard.' : response.status === 403 ? 'You do not have access to this dashboard.' : 'Unable to load dashboard data. Retry the request.');
      if (String(url).includes('/gviz')) {
        const text = await response.clone().text();
        const data = JSON.parse(text.replace(/^[^(]+\(/, '').replace(/\);?\s*$/, ''));
        if (!data.table?.rows?.length) throw new Error('No data is available. Retry after data has been loaded.');
      }
      if (disposed) throw new DOMException('Page closed', 'AbortError');
      return response;
    } catch (error) {
      if (!disposed) showError(error.name === 'AbortError' ? 'The data request timed out. Retry the request.' : error.message);
      throw error;
    } finally { options.signal?.removeEventListener('abort', abort); }
  }
  function bindExport(getRows, name) {
    root.querySelectorAll('button').forEach(button => {
      if (button.textContent.trim() !== 'Export') return;
      button.textContent = 'Export CSV';
      button.addEventListener('click', () => {
        const rows = getRows();
        if (!rows.length) { showError('There are no rows to export for the selected filters.'); return; }
        downloadCsv(rows, name);
      });
    });
  }
  return {fetch: request, showError, bindExport, onResize, parseCalendarDate, dateKey, escapeHtml, safeUrl, followerSnapshot,
    dispose() { disposed = true; controller.abort(); if (resizeHandlers.size) window.removeEventListener('resize', resize); resizeHandlers.clear(); }};
}
